import { Button, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

export interface TemplateDirOption {
    value: number;
    label: string;
}

export interface TemplateDirTypeOption {
    value: string;
    label: string;
}

export interface TemplateDirIncludeSwitch {
    id: string;
    label: string;
    hint: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
}

interface TemplateDirSelectModalProps {
    show: boolean;
    title: string;
    label: string;
    createNewLabel: string;
    options: TemplateDirOption[];
    selectedId: number;
    onSelectedIdChange: (id: number) => void;
    onHide: () => void;
    onConfirm: () => void;
    /** Optional document-type select (shown above the template select). */
    typeLabel?: string;
    typePlaceholder?: string;
    typeOptions?: TemplateDirTypeOption[];
    selectedType?: string;
    onSelectedTypeChange?: (type: string) => void;
    loadingOptions?: boolean;
    disableTemplateSelect?: boolean;
    disableConfirm?: boolean;
    /** Optional include switches (hidden when empty, e.g. create from commercial proposal). */
    includeSwitches?: TemplateDirIncludeSwitch[];
}

const TemplateDirSelectModal = ({
    show,
    title,
    label,
    createNewLabel,
    options,
    selectedId,
    onSelectedIdChange,
    onHide,
    onConfirm,
    typeLabel,
    typePlaceholder,
    typeOptions,
    selectedType,
    onSelectedTypeChange,
    loadingOptions = false,
    disableTemplateSelect = false,
    disableConfirm = false,
    includeSwitches = [],
}: TemplateDirSelectModalProps) => {
    const { t } = useTranslation(['common']);
    const showTypeSelect = Boolean(typeOptions?.length && onSelectedTypeChange);
    const showIncludeSwitches = Boolean(includeSwitches?.length);

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {showTypeSelect ? (
                    <Form.Group className="mb-3">
                        <Form.Label>{typeLabel}</Form.Label>
                        <Form.Select
                            value={selectedType || ''}
                            onChange={(e) => onSelectedTypeChange?.(e.target.value)}
                        >
                            <option value="">{typePlaceholder || ''}</option>
                            {(typeOptions || []).map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                ) : null}

                <Form.Group className={showIncludeSwitches ? 'mb-3' : 'mb-0'}>
                    <Form.Label>{label}</Form.Label>
                    <Form.Select
                        value={selectedId}
                        disabled={disableTemplateSelect || loadingOptions}
                        onChange={(e) => onSelectedIdChange(Number(e.target.value || 0))}
                    >
                        <option value={0}>{createNewLabel}</option>
                        {(options || []).map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>

                {showIncludeSwitches ? (
                    <div className="d-flex flex-column gap-3">
                        {includeSwitches.map((item) => (
                            <div key={item.id} className="form-check form-switch mb-0">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id={`template-dir-include-${item.id}`}
                                    checked={item.checked}
                                    onChange={(e) => item.onChange(Boolean(e.target.checked))}
                                />
                                <label className="form-check-label" htmlFor={`template-dir-include-${item.id}`}>
                                    {item.label}
                                </label>
                                <div className="small text-muted">{item.hint}</div>
                            </div>
                        ))}
                    </div>
                ) : null}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={onHide}>
                    {t('common:buttons.cancel')}
                </Button>
                <Button variant="primary" onClick={onConfirm} disabled={disableConfirm}>
                    {t('common:buttons.create')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default TemplateDirSelectModal;
